import { localMultiplayer, localMultiplayerParams } from './localConfig';
import { InvalidMultiplayerPacketError } from './schemaModel.js';
import {woundModel} from './woundProtocol.js';
import {bloodColorModel} from './bloodProtocol.js';
import { isSkinChunk, SkinAssembler, splitSkin } from './skinTransfer.js';
import { isCompatibleLobby, isVersionKey, JOIN_ERROR, VERSION_KEY_PATTERN, versionKey } from './lobbyPolicy.js';
import { BufferSchema } from '@geckos.io/typed-array-buffer-schema';
import { Network } from '@poki/netlib';
import { Settings } from '../Settings';
import { globalEvents } from '../utils/EventDispatcher';
import { adminIntroductionModel, changeServerLevelModel, characterModel, chatMessageModel, endCountDownMessageModel, introductionModel, levelVotesMessageModel, levelWonModel, playerPlatformModel, selectHatMessageModel, simpleMessageModel, startLoadLevelModel } from './schemas';

export const SERVER_EVENTS = {
	NETWORK_READY: 'networkReady',
	NETWORK_FAILED: 'networkFailed',
	PLAYER_JOINED: 'playerJoined',
	PLAYER_LEFT: 'playerLeft',
	PLAYER_RECONNECTING: 'playerReconnecting',
	PLAYER_INTRODUCTION: 'playerIntroduction',
	LEFT_LOBBY: 'lobbyLeft',
	HOST_CHANGED: 'hostChanged',
	LOBBY_UPDATED: 'lobbyUpdated',
	SIMPLE_MESSAGE: 'simpleMessage',
	CHANGE_LEVEL: 'changeLevel',
	START_LOAD_LEVEL: 'startLoadLevel',
	RECEIVE_SKIN: 'receiveSkin',
	LEVEL_WON: 'levelWon',
	CHAT_MESSAGE: 'chatMessage',
	END_COUNTDOWN: 'endCountDown',
	LEVEL_VOTES: 'levelVotes',
	SELECT_HAT: 'selectHat',
	PLAYER_PLATFORM: 'playerPlatform',
	WOUNDS: 'wounds',
	BLOOD_COLOR: 'bloodColor',
}

const MESSAGE_TYPE = {
	RELIABLE: 'reliable',
	UNRELIABLE: 'unreliable',
}

const GAME_ID = 'c06320df-92e9-4754-b751-0dce2e9402ec';
const CONNECT_TIMEOUT = 15000;
const MAX_EARLY_MESSAGES = 128;

const MESSAGE_EVENTS = new Map([
	[bloodColorModel.schema.id, SERVER_EVENTS.BLOOD_COLOR],
	[woundModel.schema.id, SERVER_EVENTS.WOUNDS],
	[introductionModel.schema.id, SERVER_EVENTS.PLAYER_INTRODUCTION],
	[adminIntroductionModel.schema.id, SERVER_EVENTS.PLAYER_INTRODUCTION],
	[simpleMessageModel.schema.id, SERVER_EVENTS.SIMPLE_MESSAGE],
	[changeServerLevelModel.schema.id, SERVER_EVENTS.CHANGE_LEVEL],
	[startLoadLevelModel.schema.id, SERVER_EVENTS.START_LOAD_LEVEL],
	[levelWonModel.schema.id, SERVER_EVENTS.LEVEL_WON],
	[chatMessageModel.schema.id, SERVER_EVENTS.CHAT_MESSAGE],
	[endCountDownMessageModel.schema.id, SERVER_EVENTS.END_COUNTDOWN],
	[levelVotesMessageModel.schema.id, SERVER_EVENTS.LEVEL_VOTES],
	[selectHatMessageModel.schema.id, SERVER_EVENTS.SELECT_HAT],
	[playerPlatformModel.schema.id, SERVER_EVENTS.PLAYER_PLATFORM],
]);

const VERSION_KEY = versionKey(__VERSION__);
const isNewerLobby = lobby => isVersionKey(lobby?.customData?.v) && lobby.customData.v > VERSION_KEY;

const lobbyError = code => Object.assign(new Error(code), {code});

class MultiplayerServer {
	constructor() {
		this.characterDataToProcess = [];
		this.skins = new SkinAssembler();
		this.skinTransfer = 0;

		this.n = null;
		this.ready = null;
		// Lobby requests run one at a time so a late reply never lands in a newer lobby.
		this.lobbyRequests = Promise.resolve();

		this.inLobby = false;
		this.lobbyInfo = null;
		this.leader = null;
		this.updateAvailable = false;
		this.connectedPeers = new Set();
		// Reliable messages can arrive just before netlib reports the peer connected.
		this.earlyMessages = new Map();
		this.ignoredPeers = new Set();
	}

	get id() {
		return this.n?.id || '';
	}

	get isHost() {
		return this.inLobby && !!this.leader && this.leader === this.id;
	}

	connect(){
		if (this.ready) return this.ready;
		let settle;
		const ready = new Promise((resolve, reject) => { settle = {resolve, reject}; });
		this.ready = ready;
		ready.catch(() => {
			if (this.ready !== ready) return;
			this.disconnect();
			globalEvents.dispatchEvent({type: SERVER_EVENTS.NETWORK_FAILED});
		});
		(async () => {
			const network = __LOCAL_MULTIPLAYER__ && localMultiplayer ? await this.createLocalNetwork() : new Network(GAME_ID);
			if (this.ready !== ready) {
				network.close();
				return settle.reject(lobbyError(JOIN_ERROR.CONNECTION));
			}
			this.n = network;
			const timer = setTimeout(() => settle.reject(lobbyError(JOIN_ERROR.CONNECTION)), CONNECT_TIMEOUT);
			ready.finally(() => clearTimeout(timer)).catch(() => {});
			this.initWebRTC(network, settle);
		})().catch(settle.reject);
		return ready;
	}

	async createLocalNetwork(){
		const {LocalNetwork} = await import('./localNetwork');
		const params = localMultiplayerParams;
		const value = (key, max) => Math.max(0, Math.min(max, Number(params.get(key)) || 0));
		return new LocalNetwork({latency: value('latency', 1000), jitter: value('jitter', 500), loss: value('loss', 100)});
	}

	disconnect(){
		const network = this.n;
		this.n = null;
		this.ready = null;
		this.lobbyRequests = Promise.resolve();
		this.resetLobby();
		// Netlib ignores close() until signaling has welcomed it.
		if (network?.id) network.close();
		else network?.once('ready', () => network.close());
	}

	resetLobby(){
		this.inLobby = false;
		this.lobbyInfo = null;
		this.leader = null;
		this.characterDataToProcess.length = 0;
		this.skins.clear();
		this.connectedPeers.clear();
		this.earlyMessages.clear();
		this.ignoredPeers.clear();
	}

	lobbyRequest(request){
		const result = this.lobbyRequests.then(request);
		this.lobbyRequests = result.catch(() => {});
		return result;
	}

	createLobby(settings){
		return this.lobbyRequest(async () => {
			await this.connect();
			await this.leave();
			const code = await this.n.create({codeFormat: 'short', canUpdateBy: 'leader', ...settings});
			if (!code || !this.inLobby) throw lobbyError(JOIN_ERROR.CONNECTION);
			return this.lobbyInfo;
		});
	}

	joinLobby(code, password){
		return this.lobbyRequest(async () => {
			await this.connect();
			await this.leave();
			let lobbyInfo;
			try {
				lobbyInfo = await this.n.join(code, password || undefined);
			} catch (error) {
				throw lobbyError(error?.code || JOIN_ERROR.CONNECTION);
			}
			if (!lobbyInfo) throw lobbyError(JOIN_ERROR.CONNECTION);
			if (!this.inLobby) {
				await this.leave();
				const newer = isNewerLobby(lobbyInfo);
				if (newer) this.updateAvailable = true;
				throw lobbyError(newer ? JOIN_ERROR.UPDATE_REQUIRED : JOIN_ERROR.LOBBY_OUTDATED);
			}
			return this.lobbyInfo;
		});
	}

	leaveLobby(){
		return this.lobbyRequest(() => this.leave());
	}

	async leave(){
		if (!this.n?.currentLobby && !this.inLobby) return;
		this.resetLobby();
		try {
			await this.n.leave();
		} catch (error) {
			console.warn('Could not leave multiplayer lobby', error);
		}
	}

	async listLobbies(){
		await this.connect();
		const newer = this.n.list({v: {$gt: VERSION_KEY, $regex: VERSION_KEY_PATTERN}}, undefined, 1).catch(() => []);
		let lobbies;
		try {
			lobbies = await this.n.list({v: VERSION_KEY}, undefined, 50);
		} catch (error) {
			console.warn('Filtered lobby list failed', error);
			lobbies = await this.n.list(undefined, undefined, 50);
		}
		if ((await newer).some(isNewerLobby) || lobbies.some(isNewerLobby)) this.updateAvailable = true;
		return lobbies;
	}

	async updateLobby(settings){
		if (!this.isHost) return false;
		try {
			await this.n.setLobbySettings(settings);
			return true;
		} catch (error) {
			console.warn('Could not update multiplayer lobby', error);
			return false;
		}
	}

	ignorePeer(id){
		this.ignoredPeers.add(id);
	}

	initWebRTC(network, settle){
		const on = (event, handler) => network.on(event, (...args) => {
			if (this.n === network) handler(...args);
		});
		const fail = () => {
			this.disconnect();
			settle.reject(lobbyError(JOIN_ERROR.CONNECTION));
			globalEvents.dispatchEvent({type: SERVER_EVENTS.NETWORK_FAILED});
		};

		on('ready', () => {
			settle.resolve();
			globalEvents.dispatchEvent({type:SERVER_EVENTS.NETWORK_READY});
		});
		on('failed', fail);
		on('close', fail);
		on('signalingerror', error => console.warn('Multiplayer signaling error', error?.toString?.() || error));
		on('rtcerror', error => console.warn('Multiplayer WebRTC error', error));

		on('lobby', (code, lobbyInfo) => {
			// Lobbies of other game versions cannot exchange packets with us.
			if (!isCompatibleLobby(lobbyInfo, __VERSION__)) return;
			this.inLobby = true;
			this.lobbyInfo = lobbyInfo;
			this.leader = lobbyInfo.leader || null;
		});
		on('leader', leader => {
			if (!this.inLobby || this.leader === leader) return;
			this.leader = leader;
			globalEvents.dispatchEvent({type: SERVER_EVENTS.HOST_CHANGED, id: leader});
		});
		on('lobbyUpdated', (code, lobbyInfo) => {
			if (!this.inLobby) return;
			this.lobbyInfo = lobbyInfo;
			globalEvents.dispatchEvent({type: SERVER_EVENTS.LOBBY_UPDATED, lobbyInfo});
		});
		on('left', () => {
			if (!this.inLobby) return;
			this.resetLobby();
			globalEvents.dispatchEvent({type: SERVER_EVENTS.LEFT_LOBBY});
		});

		on('connected', peer => {
			if (!this.inLobby) return;
			this.connectedPeers.add(peer.id);
			globalEvents.dispatchEvent({type:SERVER_EVENTS.PLAYER_JOINED, id: peer.id});
			const early = this.earlyMessages.get(peer.id) || [];
			this.earlyMessages.delete(peer.id);
			early.forEach(data => this.receive(peer, MESSAGE_TYPE.RELIABLE, data));
		});
		on('disconnected', peer => {
			this.connectedPeers.delete(peer.id);
			this.earlyMessages.delete(peer.id);
			this.skins.delete(peer.id);
			globalEvents.dispatchEvent({type:SERVER_EVENTS.PLAYER_LEFT, id: peer.id});
		});
		on('reconnecting', peer => globalEvents.dispatchEvent({type: SERVER_EVENTS.PLAYER_RECONNECTING, id: peer.id, reconnecting: true}));
		on('reconnected', peer => globalEvents.dispatchEvent({type: SERVER_EVENTS.PLAYER_RECONNECTING, id: peer.id, reconnecting: false}));

		on('message', (peer, channel, data) => this.receive(peer, channel, data));
	}

	receive(peer, channel, data){
		if (!this.inLobby || this.ignoredPeers.has(peer.id) || !(data instanceof ArrayBuffer) || data.byteLength < 5) return;
		if (!this.connectedPeers.has(peer.id)) {
			if (channel !== MESSAGE_TYPE.RELIABLE) return;
			const early = this.earlyMessages.get(peer.id) || [];
			if (early.length < MAX_EARLY_MESSAGES) early.push(data);
			this.earlyMessages.set(peer.id, early);
			return;
		}
		try {
			if (isSkinChunk(data)) {
				const skin = this.skins.push(peer.id, data);
				if (skin) globalEvents.dispatchEvent({type: SERVER_EVENTS.RECEIVE_SKIN, peer: peer.id, buffer: skin});
				return;
			}
			const id = BufferSchema.getIdFromBuffer(data);
			if (id === characterModel.schema.id) return this.receiveCharacterData(peer, data);
			const type = MESSAGE_EVENTS.get(id);
			if (type) globalEvents.dispatchEvent({type, peer: peer.id, buffer: data, admin: id === adminIntroductionModel.schema.id});
		} catch (error) {
			// A bad packet must not interrupt delivery of later messages.
			if (!(error instanceof InvalidMultiplayerPacketError)) throw error;
		}
	}

	getID() {
		return this.id;
	}

	send(channel, id, buffer){
		try {
			this.n?.send(channel, id, buffer);
		} catch (error) {
			console.warn('Multiplayer send failed', error);
		}
	}

	broadcast(channel, buffer){
		try {
			this.n?.broadcast(channel, buffer);
		} catch (error) {
			console.warn('Multiplayer broadcast failed', error);
		}
	}

	sendIntroduction(buffer, id){
		this.send(MESSAGE_TYPE.RELIABLE, id, buffer);
	}

	sendSkinBuffer(buffer, id){
		this.skinTransfer = (this.skinTransfer + 1) & 255;
		for (const chunk of splitSkin(buffer, this.skinTransfer)) this.send(MESSAGE_TYPE.RELIABLE, id, chunk);
	}

	sendSimpleMessageAll(buffer){
		this.broadcast(MESSAGE_TYPE.RELIABLE, buffer);
	}

	sendSimpleMessage(buffer, id){
		this.send(MESSAGE_TYPE.RELIABLE, id, buffer);
	}

	sendCharacterData(buffer) {
		this.broadcast(MESSAGE_TYPE.UNRELIABLE, buffer);
	}

	receiveCharacterData(peer, buffer) {
		if (this.characterDataToProcess.length >= 256) this.characterDataToProcess.shift();
		const samples = [peer.latency?.average, peer.latency?.last].filter(value => Number.isFinite(value) && value >= 0);
		this.characterDataToProcess.push({
			playerID: peer.id,
			buffer,
			time: performance.now(),
			ping: Math.min(...samples, Settings.maxTolerableLatency * 2) / 2,
		});
	}

	getCharacterDataToProcess() {
		const returnData = [...this.characterDataToProcess];
		this.characterDataToProcess.length = 0;
		return returnData;
	}
}

const server = new MultiplayerServer();
export default server;
